/**
 * Role token aliases for the Spanish → English refactor.
 * JWT/DB may still carry legacy tokens until re-login or a missed migration row.
 * Lookups are bidirectional: hasRoleToken(['client'], 'cliente') is true.
 */

const ROLE_ALIASES: Record<string, readonly string[]> = {
  client: ['client', 'cliente'],
  sales_rep: ['sales_rep', 'comercial', 'salesRep'],
  operations: ['operations', 'operaciones'],
  accounting: ['accounting', 'contabilidad'],
};

const CANONICAL_BY_ALIAS: Record<string, string> = {};
for (const [canonical, aliases] of Object.entries(ROLE_ALIASES)) {
  CANONICAL_BY_ALIAS[canonical] = canonical;
  for (const alias of aliases) CANONICAL_BY_ALIAS[alias] = canonical;
}

function canonicalRole(token: string): string {
  return CANONICAL_BY_ALIAS[token] ?? token;
}

function aliasesFor(canonical: string): readonly string[] {
  return ROLE_ALIASES[canonical] ?? [canonical];
}

export function hasRoleToken(roles: unknown, token: string): boolean {
  const list = Array.isArray(roles) ? roles.map((r) => String(r)) : [];
  const canonical = canonicalRole(token);
  const aliases = aliasesFor(canonical);
  return list.some((r) => aliases.includes(r));
}

export function hasAnyRoleToken(roles: unknown, tokens: string[]): boolean {
  return tokens.some((t) => hasRoleToken(roles, t));
}

/** Roles column from SQLite/JWT: JSON string, string[], or { systemRole } objects. */
export function parseStoredRoles(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((r) => {
      if (typeof r === 'string') return r;
      if (r && typeof r === 'object' && 'systemRole' in r) return String((r as { systemRole: unknown }).systemRole);
      return String(r);
    });
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      return parseStoredRoles(JSON.parse(trimmed));
    } catch {
      return [trimmed];
    }
  }
  return [];
}

/** Bootstrap adminapp must never be account-locked: unlock requires an admin session. */
export function isAdminAppAccount(rolesRaw: unknown): boolean {
  return hasRoleToken(parseStoredRoles(rolesRaw), 'adminapp');
}

export function ensureCanonicalRole(roles: string[], token: string): string[] {
  const canonical = canonicalRole(token);
  if (hasRoleToken(roles, canonical)) return roles;
  return [...roles, canonical];
}

/** SQL fragment: LOWER(column) LIKE any alias (covers salesRep vs sales_rep vs comercial). */
export function sqlRolesContains(column: string, token: string): string {
  const canonical = canonicalRole(token);
  const parts = aliasesFor(canonical).map((a) => `LOWER(${column}) LIKE '%${a.toLowerCase()}%'`);
  return `(${parts.join(' OR ')})`;
}
